/**
 * VERIFICAÇÃO DE E-MAIL COM A CARA DA PLATAFORMA — ticket 0055 do mvp-tickets.
 *
 * Este arquivo é o mesmo nas quatro plataformas (invenTAI, XequeMath, EducaHubPlay, HQ-Lab) e não
 * conhece React nem tela: é a decisão, testável sem navegador. A página `/verificar` de cada uma
 * só desenha o que sai daqui.
 *
 * DUAS METADES:
 *  1. ENVIAR. O console do Firebase não deixa editar o corpo do e-mail de verificação
 *     (support.google.com/firebase/answer/7000714), então o e-mail com a marca sai do serviço de
 *     e-mail da casa (`POST /api/verificacao/enviar`, branch `feature/verificacao-com-a-cara` do
 *     mvp-email-api). Se o serviço não responder 200, cai no envio padrão do Firebase: a pessoa
 *     NUNCA fica sem e-mail por causa da marca.
 *  2. RECEBER. O link do e-mail abre `/verificar?mode=verifyEmail&oobCode=…`. Qualquer outro
 *     `mode` (troca de senha, recuperar e-mail) é repassado para quem já trata — assim apontar a
 *     "URL de ação" do console para `/verificar` não quebra a recuperação de senha.
 */

export const SERVICO_DE_VERIFICACAO = "https://mvp-agentes.duckdns.org/api/verificacao/enviar";

/** O mínimo de um usuário do Firebase que este arquivo usa — o `User` real cabe aqui. */
export interface ContaParaVerificar {
  emailVerified: boolean;
  getIdToken(): Promise<string>;
}

export type ComoFoiEnviado = "com-a-marca" | "padrao-do-firebase" | "ja-verificado" | "aguarde";

/**
 * Pede o e-mail com a marca; se não der, manda o padrão do Firebase.
 * `enviarPeloFirebase` é `(u) => sendEmailVerification(u, { url: origem + "/" })`.
 */
export async function enviarVerificacao<U extends ContaParaVerificar>(
  conta: U,
  enviarPeloFirebase: (conta: U) => Promise<void>,
  opcoes: { servico?: string; fetch?: typeof fetch; esperaMs?: number } = {},
): Promise<ComoFoiEnviado> {
  if (conta.emailVerified) return "ja-verificado";
  const f = opcoes.fetch ?? fetch;
  try {
    const token = await conta.getIdToken();
    const corte = new AbortController();
    const relogio = setTimeout(() => corte.abort(), opcoes.esperaMs ?? 8000);
    const r = await f(opcoes.servico ?? SERVICO_DE_VERIFICACAO, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      signal: corte.signal,
    }).finally(() => clearTimeout(relogio));
    // 429 = já mandamos há menos de um minuto: mandar o do Firebase agora daria dois e-mails.
    if (r.status === 429) return "aguarde";
    if (r.ok) {
      const corpo = (await r.json().catch(() => ({}))) as { enviado?: boolean; ja_verificado?: boolean };
      if (corpo.enviado) return "com-a-marca";
      if (corpo.ja_verificado) return "ja-verificado";
    }
  } catch {
    // serviço fora do ar, sem rede, CORS: cai no padrão abaixo
  }
  await enviarPeloFirebase(conta);
  return "padrao-do-firebase";
}

export type LinkDeAcao =
  | { tipo: "verificar"; codigo: string; depois: string }
  | { tipo: "repassar"; destino: string }
  | { tipo: "incompleto" };

/** Só volta para dentro da própria plataforma — nunca um redirecionamento aberto. */
export function destinoSeguro(continueUrl: string | null, origem: string, padrao = "/"): string {
  if (!continueUrl) return padrao;
  try {
    const u = new URL(continueUrl, origem);
    if (u.origin !== new URL(origem).origin) return padrao;
    const caminho = u.pathname + u.search + u.hash;
    return caminho.startsWith("/verificar") ? padrao : caminho;
  } catch {
    return padrao;
  }
}

/**
 * Lê o link que chegou. `repassar(mode)` diz para onde vai cada `mode` que não é verifyEmail;
 * se devolver null, vai para a página padrão do Firebase (`https://<authDomain>/__/auth/action`).
 */
export function lerLinkDeAcao(
  search: string,
  ctx: { origem: string; authDomain: string; repassar?: (mode: string) => string | null },
): LinkDeAcao {
  const q = new URLSearchParams(search);
  const mode = q.get("mode");
  const codigo = q.get("oobCode");
  if (!mode || !codigo) return { tipo: "incompleto" };
  if (mode === "verifyEmail") return { tipo: "verificar", codigo, depois: destinoSeguro(q.get("continueUrl"), ctx.origem) };
  const local = ctx.repassar?.(mode);
  const resto = q.toString();
  return { tipo: "repassar", destino: local ? `${local}?${resto}` : `https://${ctx.authDomain}/__/auth/action?${resto}` };
}

export type Desfecho = "verificado" | "vencido" | "usado-ou-invalido" | "conta-sumiu" | "sem-rede" | "muitas-tentativas" | "erro";

/** Tradução do erro do `applyActionCode` — a tela nunca mostra o código cru do Firebase. */
export function desfechoDoErro(erro: unknown): Desfecho {
  const code = String((erro as { code?: string } | null)?.code ?? "");
  switch (code) {
    case "auth/expired-action-code": return "vencido";
    case "auth/invalid-action-code": return "usado-ou-invalido";
    case "auth/user-not-found":
    case "auth/user-disabled": return "conta-sumiu";
    case "auth/network-request-failed": return "sem-rede";
    case "auth/too-many-requests": return "muitas-tentativas";
    default: return "erro";
  }
}

/** O texto de cada desfecho, no tom de cada plataforma (cada página passa o seu `nome`). */
export function textoDoDesfecho(d: Desfecho | "conferindo" | "incompleto", nome: string): { titulo: string; texto: string } {
  switch (d) {
    case "conferindo": return { titulo: "Conferindo o seu link…", texto: "Isso leva só um instante." };
    case "verificado": return { titulo: "E-mail confirmado!", texto: `Sua conta no ${nome} está pronta. Agora é só entrar.` };
    case "vencido": return { titulo: "Este link venceu", texto: "Os links de confirmação valem por pouco tempo. Entre com seu e-mail e senha e peça um novo — ele chega em instantes." };
    case "usado-ou-invalido": return { titulo: "Este link já foi usado", texto: "Se você já confirmou o e-mail, é só entrar. Se ainda não, entre com seu e-mail e senha e peça um novo link." };
    case "conta-sumiu": return { titulo: "Não achamos esta conta", texto: `A conta deste link não existe mais no ${nome}. Você pode criar uma nova.` };
    case "sem-rede": return { titulo: "Sem conexão", texto: "Não deu para falar com o servidor. Confira a internet e tente de novo — o link continua valendo." };
    case "muitas-tentativas": return { titulo: "Muitas tentativas seguidas", texto: "Espere alguns minutos e abra o link de novo." };
    case "incompleto": return { titulo: "Link incompleto", texto: "Este endereço chegou cortado. Abra o link direto do e-mail, ou copie o endereço inteiro." };
    default: return { titulo: "Não deu para confirmar agora", texto: "Tente abrir o link de novo em instantes. Se continuar, peça um novo e-mail ao entrar." };
  }
}
